import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, CardBody, Button, Badge, Alert } from 'reactstrap'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../../AbstractElements'
import HeaderCard from '../Common/Component/HeaderCard'
import { getIdData } from '../../lib/studentinfo'
import { useNavigate, useParams } from 'react-router-dom'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { StudentTopMenuLayout } from '../../Layout/StudentTopMenu/StudentTopMenuLayout'
import StudentInfoBox from './StudentInfoBox'

const AdminStudentDetail = () => {
	const [studentInfo, setStudentInfo] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')

	const navigate = useNavigate()
	const { id } = useParams()
	const layout = useLayout()
	const role = useRoleBasedUrl()

	const fetchStudentData = async () => {
		try {
			setLoading(true)
			const info = await getIdData(id)
			setStudentInfo(info)
		} catch (error) {
			console.error('Error fetching student data:', error)
			setError('Failed to load student information')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchStudentData()
	}, [id])

	if (loading) {
		return (
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card>
							<CardBody className="text-center">
								<div className="spinner-border" role="status">
									<span className="visually-hidden">Loading...</span>
								</div>
								<p className="mt-2">Loading student information...</p>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		)
	}

	if (error || !studentInfo || !studentInfo[0]) {
		return (
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card>
							<CardBody className="text-center">
								<Alert color="danger">{error || 'Student not found'}</Alert>
								<Button color="primary" onClick={() => navigate(-1)}>
									Go Back
								</Button>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		)
	}

	const student = studentInfo[0]

	return (
		<>
			<Breadcrumbs parent="Student Management" title="Student Details" mainTitle="Student Information" />
			<StudentTopMenuLayout activeMenu="Detail" pageName="StudentDetail" showPageNotes={false}>
				<Container fluid={true}>
					<Row>
						<Col sm="12">
							<StudentInfoBox slug={id} />

							<Card>
								<HeaderCard title="Student Details" subTitle="View student information" />
								<CardBody>
									<Row>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">First Name</label>
												<p className="mb-0">{student.name || 'N/A'}</p>
											</div>
										</Col>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">Last Name</label>
												<p className="mb-0">{student.surname || 'N/A'}</p>
											</div>
										</Col>
									</Row>

									<Row>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">Phone</label>
												<p className="mb-0">{student.phone || 'N/A'}</p>
											</div>
										</Col>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">Email</label>
												<p className="mb-0">{student.email || 'N/A'}</p>
											</div>
										</Col>
									</Row>

									<Row>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">School Name</label>
												<p className="mb-0">{student.schoolname || 'N/A'}</p>
											</div>
										</Col>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">Grade Level</label>
												<p className="mb-0">
													<Badge color="info" className="px-2 py-1">
														{student.class || 'N/A'}
													</Badge>
												</p>
											</div>
										</Col>
									</Row>

									<Row>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">Status</label>
												<p className="mb-0">
													<Badge color={student.isActive ? 'success' : 'secondary'} className="px-2 py-1">
														{student.isActive ? 'Active' : 'Inactive'}
													</Badge>
												</p>
											</div>
										</Col>
										<Col lg="6">
											<div className="form-group mb-3">
												<label className="form-control-label fw-bold">Username</label>
												<p className="mb-0">{student.username || 'N/A'}</p>
											</div>
										</Col>
									</Row>

									{student.gpa && (
										<Row>
											<Col lg="6">
												<div className="form-group mb-3">
													<label className="form-control-label fw-bold">GPA</label>
													<p className="mb-0">{student.gpa}</p>
												</div>
											</Col>
											<Col lg="6">
												<div className="form-group mb-3">
													<label className="form-control-label fw-bold">Graduation Year</label>
													<p className="mb-0">{student.graduateyear || 'N/A'}</p>
												</div>
											</Col>
										</Row>
									)}

									{student.major && (
										<Row>
											<Col lg="6">
												<div className="form-group mb-3">
													<label className="form-control-label fw-bold">Major</label>
													<p className="mb-0">{student.major}</p>
												</div>
											</Col>
											<Col lg="6">
												<div className="form-group mb-3">
													<label className="form-control-label fw-bold">Career</label>
													<p className="mb-0">{student.career || 'N/A'}</p>
												</div>
											</Col>
										</Row>
									)}

									<div className="d-flex justify-content-between mt-4">
										<Button color="primary" onClick={() => navigate(`${process.env.PUBLIC_URL}/${role}/students/edit/${id}/Update/${layout}`)}>
											Update Profile
										</Button>
										<Link to={`${process.env.PUBLIC_URL}/${role}/students/role-change/${id}/${layout}`} className="btn btn-info">
											Update Role
										</Link>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</StudentTopMenuLayout>
		</>
	)
}

export { AdminStudentDetail }
